export type TeamRole = "admin" | "editor" | "viewer";
export type EffectiveRole = TeamRole | "owner";

export const TEAM_ROLES: TeamRole[] = ["admin", "editor", "viewer"];

export const ROLE_META: Record<
  EffectiveRole,
  { label: string; description: string; badgeClass: string }
> = {
  owner: {
    label: "Owner",
    description: "Full control, including renaming or deleting the team.",
    badgeClass: "bg-primary/15 text-primary border-primary/30",
  },
  admin: {
    label: "Admin",
    description: "Can invite, remove and change roles of team members.",
    badgeClass: "bg-accent/15 text-accent-foreground border-accent/40",
  },
  editor: {
    label: "Editor",
    description: "Can create and edit team data, but not manage members.",
    badgeClass: "bg-secondary text-secondary-foreground border-border",
  },
  viewer: {
    label: "Viewer",
    description: "Read-only access to team data.",
    badgeClass: "bg-muted text-muted-foreground border-border",
  },
};

export type TeamAction =
  | "viewTeam"
  | "manageMembers"
  | "editData"
  | "renameTeam"
  | "deleteTeam";

const MATRIX: Record<EffectiveRole, TeamAction[]> = {
  owner: ["viewTeam", "manageMembers", "editData", "renameTeam", "deleteTeam"],
  admin: ["viewTeam", "manageMembers", "editData"],
  editor: ["viewTeam", "editData"],
  viewer: ["viewTeam"],
};

export const can = (role: EffectiveRole | null, action: TeamAction): boolean =>
  !!role && MATRIX[role].includes(action);

export const canManageMembers = (role: EffectiveRole | null) => can(role, "manageMembers");
export const canEditData = (role: EffectiveRole | null) => can(role, "editData");
export const canDeleteTeam = (role: EffectiveRole | null) => can(role, "deleteTeam");

export const normalizeRole = (role?: string | null): TeamRole =>
  TEAM_ROLES.includes(role as TeamRole) ? (role as TeamRole) : "viewer";
